import { useQuery } from "@tanstack/react-query";
import { guestGetNotes } from "../api/meetingsApi";
import type { NotePhase } from "../types";
import { formatDateTime } from "@/utils/formatDate";

const PHASE_LABELS: Record<NotePhase, string> = {
  before: "Przed",
  during: "W trakcie",
  after: "Po",
};

/**
 * Read-only notes for an external guest. Only reachable when the organizer has
 * unlocked notes for guests (the backend 403s otherwise). Polls, since the
 * guest has no authenticated realtime subscription for the notes query.
 */
export function GuestNotesPanel({ token }: { token: string }) {
  // Primary refresh is live via Centrifugo (the room channel's note_changed
  // event invalidates this query — see GuestMeetingPage.onNoteChanged); the
  // slow interval is just a safety net.
  const { data: notes = [], isError } = useQuery({
    queryKey: ["guest-notes", token],
    queryFn: () => guestGetNotes(token),
    refetchInterval: 60000,
    retry: false,
  });

  return (
    <div className="space-y-3 p-3">
      <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
        Notatki
      </h2>
      {isError && (
        <p className="text-sm text-[var(--color-text-muted)]">
          Notatki nie są udostępnione.
        </p>
      )}
      {!isError && notes.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">Brak notatek.</p>
      )}
      {notes.map((note) => (
        <div
          key={note.id}
          className="rounded-md border border-[var(--color-border)] p-3"
        >
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <span className="font-medium text-[var(--color-text-secondary)]">
              {note.author?.name ?? "—"}
            </span>
            <span>· {formatDateTime(note.created_at)}</span>
            <span className="rounded bg-black/5 px-1.5 py-0.5 dark:bg-white/10">
              {PHASE_LABELS[note.phase]}
            </span>
            {note.is_edited && <span>(edytowano)</span>}
          </div>
          <div
            className="prose prose-sm max-w-none text-sm text-[var(--color-text-primary)] dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: note.content }}
          />
        </div>
      ))}
    </div>
  );
}
